import { useEffect, useState, useRef, useCallback } from "react";
import Layout from "../shared/Layout";
import { Toaster, toast } from "react-hot-toast";
import DataTable from "react-data-table-component";
import axiosIns, { API_URL } from "../../_config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { decryptData } from "../../_seivices";
import LoadingButton from "../shared/LoadingButton";
import useAsyncSubmit from "../shared/useAsyncSubmit";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const HTML_SECTION_FIELDS = [
    {
        key: "whyThisServiceMatters",
        label: "Why This Service Matters",
        titleField: "whyThisServiceMattersTitle",
        htmlField: "whyThisServiceMattersHtml",
        defaultTitle: "Why This Service Matters"
    },
    {
        key: "whatWeOffer",
        label: "What We Offer",
        titleField: "whatWeOfferTitle",
        htmlField: "whatWeOfferHtml",
        defaultTitle: "What We Offer"
    },
    {
        key: "benefits",
        label: "Benefits",
        titleField: "benefitsTitle",
        htmlField: "benefitsHtml",
        defaultTitle: "Benefits"
    },
    {
        key: "technologies",
        label: "Technologies",
        titleField: "technologiesTitle",
        htmlField: "technologiesHtml",
        defaultTitle: "Technologies"
    },
    {
        key: "whyChooseIshivax",
        label: "Why Choose iShivax",
        titleField: "whyChooseIshivaxTitle",
        htmlField: "whyChooseIshivaxHtml",
        defaultTitle: "Why Choose iShivax"
    }
];

const CUSTOM_SECTION_FIELDS = [
    { label: "Title", name: "title" },
    { label: "Subtitle", name: "subtitle" },
    { label: "Description", name: "description" }
];

const createEmptyCustomSection = () => ({
    sectionName: "",
    fields: CUSTOM_SECTION_FIELDS.map(field => ({
        ...field,
        type: "text",
        value: "",
        file: null
    }))
});

const createEmptyQuickFact = () => ({
    feature: "",
    details: "",
    image: null,
    color: null,
    file: null
});

const createEmptyFactsFeature = () => ({
    title: "",
    details: ""
});

const getQuickFactRows = (rows = []) => (
    rows.length
        ? rows.map(item => ({
            feature: item?.feature || "",
            details: item?.details || "",
            image: item?.image || null,
            color: item?.color || null,
            file: null
        }))
        : [createEmptyQuickFact()]
);

const getFactsFeatureRows = (rows = []) => (
    rows.length
        ? rows.map(item => ({
            title: item?.title || "",
            details: item?.details || ""
        }))
        : [createEmptyFactsFeature()]
);

const getColorPickerValue = (value) => (
    /^#[0-9a-f]{6}$/i.test(value || "") ? value : "#000000"
);

const getOptionalQuickFactValue = (value) => (
    value === undefined || value === null || value === "" ? null : value
);

const getCustomFieldValue = (field) => {
    const value = field?.value;

    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);

    return String(value).trim();
};

const getAddedCustomSections = (customSections = []) => (
    customSections.filter(section => {
        const sectionName = section?.sectionName?.trim();
        const hasValue = (section?.fields || []).some(field => getCustomFieldValue(field));

        return sectionName && hasValue;
    })
);

const getCustomSectionNames = (rows = []) => {
    const sectionNames = new Map();

    rows.forEach(row => {
        getAddedCustomSections(row?.customSections || []).forEach(section => {
            const sectionName = section.sectionName.trim();
            const key = sectionName.toLowerCase();

            if (!sectionNames.has(key)) {
                sectionNames.set(key, sectionName);
            }
        });
    });

    return Array.from(sectionNames.values());
};

const getCustomSectionValues = (row, sectionName) => (
    getAddedCustomSections(row?.customSections || [])
        .filter(section => section.sectionName.trim().toLowerCase() === sectionName.toLowerCase())
        .flatMap(section => section.fields || [])
        .map(field => getCustomFieldValue(field))
        .filter(Boolean)
);

const getFixedCustomSectionFields = (fields = []) => (
    CUSTOM_SECTION_FIELDS.map(fieldConfig => {
        const savedField = fields.find(field => (
            field?.name?.toLowerCase() === fieldConfig.name
            || field?.label?.toLowerCase() === fieldConfig.label.toLowerCase()
        ));

        return {
            ...fieldConfig,
            type: "text",
            value: savedField?.value || "",
            file: null
        };
    })
);

const createEmptyFormData = (serviceId = "") => ({
    id: 0,
    serviceId: serviceId || "",
    sectionOneTitle: "",
    sectionOneSubTitle: "",
    sectionOneShortDescription: "",
    sectionOneImage: "",
    file: null,
    developmentProcessTitle: "Development Process",
    developmentProcessImage: null,

    sectionTwoTitle: "",
    sectionTwoSubTitle: "",
    sectionTwoDescription: "",
    industriesTitle: "",
    industriesSubTitle: "",

    AIAnswerBoxTitle: "",
    AIAnswerBoxData: [{ q: "", answer: "" }],
    quickFactsTitle: "Quick Facts",
    quickFactsData: [createEmptyQuickFact()],
    caseStudiesTitle: "Case Studies",
    caseStudiesData: [{ name: "", link: "" }],
    factsFeaturesTitle: "Facts Features",
    factsFeaturesData: [createEmptyFactsFeature()],
    whyThisServiceMattersTitle: "Why This Service Matters",
    whyThisServiceMattersHtml: "",
    whatWeOfferTitle: "What We Offer",
    whatWeOfferHtml: "",
    benefitsTitle: "Benefits",
    benefitsHtml: "",
    technologiesTitle: "Technologies",
    technologiesHtml: "",
    whyChooseIshivaxTitle: "Why Choose iShivax",
    whyChooseIshivaxHtml: "",
    customSections: []
});

const getServiceDetailFormPath = (serviceId, mode, detailId = "") => {
    const basePath = serviceId ? `/service-details/${serviceId}` : "/service-details";

    if (mode === "add") return `${basePath}/add`;
    if (mode === "edit") return `${basePath}/edit/${detailId}`;

    return basePath;
};

const mapServiceDetailToFormData = (item, serviceId = "") => ({
    id: item?.id || 0,
    serviceId: item?.serviceId || serviceId || "",
    sectionOneTitle: item?.sectionOne?.title || "",
    sectionOneSubTitle: item?.sectionOne?.sectionOneSubTitle || "",
    sectionOneShortDescription: item?.sectionOne?.shortDescrition || "",
    sectionOneImage: "",
    file: null,
    developmentProcessTitle: item?.developmentProcess?.title || "Development Process",
    developmentProcessImage: null,

    sectionTwoTitle: item?.sectionTwo?.title || "",
    sectionTwoSubTitle: item?.sectionTwo?.subTitle || "",
    sectionTwoDescription: item?.sectionTwo?.description || "",
    industriesTitle: item?.sectionThird?.title || "",
    industriesSubTitle: item?.sectionThird?.subTitle || "",
    AIAnswerBoxTitle: item?.AIAnswerBox?.title || "",
    AIAnswerBoxData: item?.AIAnswerBox?.data?.length ? item.AIAnswerBox.data : [{ q: "", answer: "" }],
    quickFactsTitle: item?.quickFacts?.title || "Quick Facts",
    quickFactsData: getQuickFactRows(item?.quickFacts?.data || []),
    caseStudiesTitle: item?.caseStudies?.title || "Case Studies",
    caseStudiesData: item?.caseStudies?.data?.length ? item.caseStudies.data : [{ name: "", link: "" }],
    factsFeaturesTitle: item?.facts_features?.title || item?.factsFeatures?.title || "Facts Features",
    factsFeaturesData: getFactsFeatureRows(item?.facts_features?.data || item?.factsFeatures?.data || []),
    ...HTML_SECTION_FIELDS.reduce((sections, section) => ({
        ...sections,
        [section.titleField]: item?.htmlSections?.[section.key]?.title || section.defaultTitle,
        [section.htmlField]: item?.htmlSections?.[section.key]?.html || ""
    }), {}),
    customSections: item?.customSections?.length ? item.customSections.map(section => ({
        sectionName: section?.sectionName || "",
        fields: getFixedCustomSectionFields(section?.fields || [])
    })) : []
});

const ServiceDetails = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const serviceId = params?.id;
    const detailId = params?.detailId;
    const isAddPage = location.pathname.endsWith("/add");
    const isEditPage = Boolean(detailId);
    const isFormPage = isAddPage || isEditPage;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [serviceData, setServiceData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fileInputRef = useRef(null);
    const developmentProcessImageInputRef = useRef(null);
    const { isSubmitting, run: runSubmit } = useAsyncSubmit();

    const [formData, setFormData] = useState(createEmptyFormData(serviceId));

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : (type === 'file' ? files[0] : value)
        }));
    };

    const handleHtmlChange = (name, value) => {
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleArrayChange = (sectionName, index, fieldName, value) => {
        setFormData(prevState => ({
            ...prevState,
            [sectionName]: prevState[sectionName].map((item, itemIndex) => (
                itemIndex === index ? { ...item, [fieldName]: value } : item
            ))
        }));
    };

    const addArrayRow = (sectionName, emptyRow) => {
        setFormData(prevState => ({
            ...prevState,
            [sectionName]: [...prevState[sectionName], emptyRow]
        }));
    };

    const removeArrayRow = (sectionName, index, emptyRow) => {
        setFormData(prevState => {
            const nextRows = prevState[sectionName].filter((_, itemIndex) => itemIndex !== index);

            return {
                ...prevState,
                [sectionName]: nextRows.length ? nextRows : [emptyRow]
            };
        });
    };

    const handleCustomSectionNameChange = (sectionIndex, value) => {
        setFormData(prevState => ({
            ...prevState,
            customSections: prevState.customSections.map((section, index) => (
                index === sectionIndex ? { ...section, sectionName: value } : section
            ))
        }));
    };

    const handleCustomFieldChange = (sectionIndex, fieldIndex, fieldName, value) => {
        setFormData(prevState => ({
            ...prevState,
            customSections: prevState.customSections.map((section, index) => (
                index === sectionIndex
                    ? {
                        ...section,
                        fields: section.fields.map((field, currentFieldIndex) => (
                            currentFieldIndex === fieldIndex ? { ...field, [fieldName]: value } : field
                        ))
                    }
                    : section
            ))
        }));
    };

    const addCustomSection = () => {
        setFormData(prevState => ({
            ...prevState,
            customSections: [...prevState.customSections, createEmptyCustomSection()]
        }));
    };

    const buildServiceDetailPayload = (sourceFormData) => {
        const payload = new FormData();
        const filteredAIAnswerBoxData = sourceFormData.AIAnswerBoxData.filter(item => item.q || item.answer);
        const filteredQuickFactsData = sourceFormData.quickFactsData
            .filter(item => item.feature || item.details || item.image || item.color || item.file)
            .map((item, index) => {
                const nextItem = {
                    feature: item.feature || "",
                    details: item.details || "",
                    image: item.file ? null : getOptionalQuickFactValue(item.image),
                    color: getOptionalQuickFactValue(item.color)
                };

                if (item.file) {
                    const uploadFieldName = `quickFactImage-${index}`;
                    payload.append(uploadFieldName, item.file);
                    nextItem.uploadFieldName = uploadFieldName;
                }

                return nextItem;
            });
        const filteredCaseStudiesData = sourceFormData.caseStudiesData.filter(item => item.name || item.link);
        const filteredFactsFeaturesData = sourceFormData.factsFeaturesData.filter(item => item.title || item.details);
        let customImageIndex = 0;
        const filteredCustomSections = sourceFormData.customSections
            .map(section => ({
                sectionName: section.sectionName,
                fields: section.fields
                    .filter(field => field.label || field.name || field.value || field.file)
                    .map(field => {
                        const nextField = {
                            label: field.label,
                            name: field.name,
                            type: field.type,
                            value: field.type === "image" && field.file ? "" : field.value
                        };

                        if (field.type === "image" && field.file) {
                            const uploadFieldName = `customSectionImage-${customImageIndex}`;
                            payload.append(uploadFieldName, field.file);
                            nextField.uploadFieldName = uploadFieldName;
                            customImageIndex += 1;
                        }

                        return nextField;
                    })
            }))
            .filter(section => section.sectionName || section.fields.length);

        Object.entries(sourceFormData).forEach(([key, value]) => {
            if (key === "AIAnswerBoxData" || key === "quickFactsData" || key === "caseStudiesData" || key === "factsFeaturesData" || key === "customSections") return;
            if (key === "file" || key === "developmentProcessImage") {
                if (value) payload.append(key, value);
                return;
            }
            if (value !== null && value !== undefined) {
                payload.append(key, value);
            }
        });

        payload.append("AIAnswerBoxData", JSON.stringify(filteredAIAnswerBoxData));
        payload.append("quickFactsData", JSON.stringify(filteredQuickFactsData));
        payload.append("caseStudiesData", JSON.stringify(filteredCaseStudiesData));
        payload.append("factsFeaturesData", JSON.stringify(filteredFactsFeaturesData));
        payload.append("customSections", JSON.stringify(filteredCustomSections));

        return payload;
    };

    const saveServiceDetail = (sourceFormData) => (
        axiosIns.post(API_URL.SaveServiceDetail, buildServiceDetailPayload(sourceFormData), {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    );

    const removeCustomSection = async (sectionIndex) => {
        const nextFormData = {
            ...formData,
            customSections: formData.customSections.filter((_, index) => index !== sectionIndex)
        };
        const previousFormData = formData;

        setFormData(nextFormData);

        if (!nextFormData.id) return;

        try {
            const res = await saveServiceDetail(nextFormData);

            if (res?.data?.isSuccess === 1) {
                toast.success("Section removed successfully");
                await fetchData();
            } else {
                setFormData(previousFormData);
                toast.error(res?.data?.message || "Remove section failed");
            }
        } catch (error) {
            setFormData(previousFormData);
            toast.error("Remove section failed");
        }
    };

    // Fetch Creative list
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosIns.get(`${API_URL.GetServiceDetailByServiceId}?id=${serviceId}`);

            if (res?.data?.isSuccess === 1) {
                if (res?.data?.isSuccess === 1) {
                    const apiData = res?.data?.data;

                    // → If array: use it
                    // → If object: wrap into array
                    // → If null: empty array
                    const normalizedData =
                        Array.isArray(apiData)
                            ? apiData
                            : apiData && typeof apiData === "object"
                                ? [apiData]
                                : [];

                    setData(normalizedData);
                }
            }
        } catch (error) {
            setData([]);
        }
        setLoading(false);
    }, [serviceId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch Services
    const fetchServiceData = async () => {
        try {
            const response = await axiosIns.get(API_URL.getServices);

            // ✅ Get encrypted payload from BODY (not header)
            const encrypted = response?.data?.payload;

            if (!encrypted) {
                setServiceData([]);
                return;
            }

            // ✅ Decrypt
            const decrypted = decryptData(encrypted);

            // ✅ Validate
            if (decrypted?.isSuccess === 1 && Array.isArray(decrypted.data)) {
                setServiceData(decrypted.data);
            } else {
                setServiceData([]);
            }

        } catch (error) {
            console.error("Fetch services error:", error);
            setServiceData([]);
        }
    };

    useEffect(() => {
        fetchServiceData();
    }, []);

    const onSave = async (e) => {
        e.preventDefault();
        await runSubmit(async () => {
            try {
                const res = await saveServiceDetail(formData);

                if (res?.data?.isSuccess === 1) {
                    toast.success(res.data.message);
                    await fetchData();
                    resetForm();
                    navigate(getServiceDetailFormPath(formData.serviceId || serviceId));
                }
            } catch (error) {
                toast.error("Something went wrong!");
            }
        });
    };

    const onEdit = (item) => {
        navigate(getServiceDetailFormPath(item?.serviceId || serviceId, "edit", item?.id), {
            state: { serviceDetail: item }
        });
    };

    // Delete Creative
    const onDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete?")) return;

        try {
            const res = await axiosIns.get(`${API_URL.DeleteServiceDetail}?id=${id}`);

            if (res?.data?.isSuccess === 1) {
                toast.success("Deleted successfully");
                fetchData();
            }
        } catch (error) {
            toast.error("Delete failed");
        }
    };

    const resetForm = useCallback(() => {
        setFormData(createEmptyFormData(serviceId));

        if (fileInputRef.current) fileInputRef.current.value = "";
        if (developmentProcessImageInputRef.current) developmentProcessImageInputRef.current.value = "";
    }, [serviceId]);

    useEffect(() => {
        if (isAddPage) {
            resetForm();
        }
    }, [isAddPage, resetForm, serviceId]);

    useEffect(() => {
        if (!isEditPage) return;

        const routeStateItem = location.state?.serviceDetail;
        const fetchedItem = data.find(detail => detail?.id?.toString() === detailId?.toString());
        const routeItem = routeStateItem?.id?.toString() === detailId?.toString()
            ? routeStateItem
            : null;
        const item = fetchedItem || routeItem;

        if (item) {
            setFormData(mapServiceDetailToFormData(item, serviceId));
        }
    }, [data, detailId, isEditPage, location.state, serviceId]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage, page) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(page);
    };

    const renderHtmlSectionEditor = (section) => (
        <div className="border rounded p-3 mb-3" key={section.key}>
            <h4>{section.label}</h4>
            <div className="form-group">
                <label>{section.label} Title</label>
                <input
                    type="text"
                    name={section.titleField}
                    className="form-control"
                    value={formData[section.titleField]}
                    onChange={handleChange}
                    placeholder={`Enter ${section.label} Title`}
                />
            </div>
            <div className="form-group">
                <label>{section.label} HTML</label>
                <ReactQuill
                    theme="snow"
                    value={formData[section.htmlField]}
                    onChange={(value) => handleHtmlChange(section.htmlField, value)}
                />
            </div>
        </div>
    );

    const renderCustomSectionsEditor = () => (
        <div className="custom-sections-editor">
            {formData.customSections.length > 0 && (
                <h4 className="mb-2">Custom Sections</h4>
            )}

            {formData.customSections.map((section, sectionIndex) => (
                <div className="border rounded p-3 mb-3" key={`custom-section-${sectionIndex}`}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h4 className="mb-0">Section {sectionIndex + 1}</h4>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeCustomSection(sectionIndex)}
                        >
                            Remove Section
                        </button>
                    </div>

                    <div className="form-group">
                        <label>Section Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={section.sectionName}
                            onChange={(e) => handleCustomSectionNameChange(sectionIndex, e.target.value)}
                            placeholder="Enter Section Name"
                        />
                    </div>

                    <div className="custom-section-field border rounded p-3 mb-3">
                        {section.fields.map((field, fieldIndex) => (
                            <div className="form-group" key={`custom-field-${sectionIndex}-${field.name}`}>
                                <label>{field.label}</label>
                                {field.name === "description" ? (
                                    <textarea
                                        className="form-control"
                                        value={field.value}
                                        onChange={(e) => handleCustomFieldChange(sectionIndex, fieldIndex, "value", e.target.value)}
                                        placeholder={`Enter ${field.label}`}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={field.value}
                                        onChange={(e) => handleCustomFieldChange(sectionIndex, fieldIndex, "value", e.target.value)}
                                        placeholder={`Enter ${field.label}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    const customSectionColumns = getCustomSectionNames(data).map(sectionName => ({
        name: sectionName,
        cell: (row) => {
            const values = getCustomSectionValues(row, sectionName);

            return (
                <div className="service-detail-custom-cell">
                    {values.length ? values.join(", ") : "-"}
                </div>
            );
        },
        width: "220px"
    }));

    // Datatable columns
    const columns = [
        {
            name: "SN",
            selector: (row, i) => ((currentPage - 1) * rowsPerPage) + i + 1,
            width: "70px"
        },
        {
            name: "Service Name",
            selector: (row) => row?.serviceName,
            sortable: true,
            width: "160px"
        },
        {
            name: "Section One Image",
            selector: (row) =>
                row?.sectionOne?.image ? <img src={row?.sectionOne?.image} width="60" alt="" /> : "No Image",
            width: "150px"
        },
        {
            name: "Section One Title",
            selector: (row) => row?.sectionOne?.title,
            sortable: true,
            width: "160px"
        },
        {
            name: "Section One SubTitle",
            selector: (row) => row?.sectionOne?.sectionOneSubTitle,
            sortable: true,
            width: "180px"
        },
        {
            name: "Section One Description",
            selector: (row) => row?.sectionOne?.shortDescrition,
            width: "500px"
        },
        {
            name: "Section Two Title",
            selector: (row) => row?.sectionTwo?.title,
            sortable: true,
            width: "160px"
        },
        {
            name: "Section Two SubTitle",
            selector: (row) => row?.sectionTwo?.subTitle,
            width: "180px"
        },
        {
            name: "Section Two Description",
            selector: (row) => row?.sectionTwo?.description,
            width: "200px"
        },
        {
            name: "Section Third Title",
            selector: (row) => row?.sectionThird?.title,
            sortable: true,
            width: "160px"
        },
        {
            name: "Section Third SubTitle",
            selector: (row) => row?.sectionThird?.subTitle,
            width: "180px"
        },
        {
            name: "AI Answer Box",
            selector: (row) => row?.AIAnswerBox?.title,
            width: "180px"
        },
        {
            name: "Quick Facts",
            selector: (row) => row?.quickFacts?.title,
            width: "180px"
        },
        {
            name: "Case Studies",
            selector: (row) => row?.caseStudies?.title,
            width: "180px"
        },
        {
            name: "Facts Features",
            selector: (row) => row?.facts_features?.title,
            width: "180px"
        },
        {
            name: "Development Process",
            selector: (row) =>
                row?.developmentProcess?.image
                    ? <img src={row.developmentProcess.image} width="60" alt="" />
                    : row?.developmentProcess?.title,
            width: "180px"
        },
        ...HTML_SECTION_FIELDS.map(section => ({
            name: section.label,
            cell: (row) => (
                <div
                    className="service-detail-html-cell"
                    dangerouslySetInnerHTML={{ __html: row?.htmlSections?.[section.key]?.html || "" }}
                />
            ),
            width: "220px"
        })),
        ...customSectionColumns,
        {
            name: 'Actions',
            width: '100px',
            cell: row => (
                <div className='d-flex gap-2'>
                    <button onClick={() => onEdit(row)} className='edit_icon_wepper'> <svg id="Layer_1" viewBox="0 0 512 512" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                        <g transform="matrix(1,0,0,1,0,0)">
                            <path d="m41.3 512c-22.8 0-41.3-18.5-41.3-41.3 0-2.6.2-5.2.7-7.7l27.3-144.8c1.5-8.2 5.5-15.7 11.4-21.5l274-274c14.7-14.7 35.1-22.7 57.7-22.7 8.1 0 16.1 1 24 2.8 25.5 5.9 51.1 20.7 72.2 41.8s36 46.7 41.8 72.2c7.4 31.9.2 61.7-19.8 81.7l-274 274c-5.9 5.9-13.4 9.8-21.5 11.4l-144.8 27.4c-2.5.5-5.1.7-7.7.7zm51.8-93.1 72.9-13.8 182-182-59.1-59.1-182 182zm313.3-254.2 23.1-23.1c.2-5.4-3.8-21.8-20.6-38.5-10.1-10.2-22-17.4-32.4-19.8-1.8-.4-3.7-.7-5.5-.7h-.7l-23 23z" fill="#526484" />
                            <path d="m499.4 119.1c-5.4-23.7-19.4-47.6-39.1-67.4s-43.8-33.7-67.4-39.1c-28.5-6.6-54.9-.4-72.3 17.1l-274 274c-4.5 4.4-7.5 10.1-8.7 16.3l-27.4 144.9c-3.2 17 8 33.4 24.9 36.6 1.9.4 3.9.5 5.8.5s3.9-.2 5.8-.5l145-27.4c6.2-1.2 11.9-4.2 16.3-8.6l274-274c17.5-17.5 23.7-43.9 17.1-72.4zm-328.5 295.3-90.3 17 17-90.3 191.3-191.3 73.3 73.3zm267.2-267.2-31.7 31.7-73.2-73.3 31.7-31.7c1-1 5.6-2.3 14-.4 12.2 2.8 25.8 11 37.2 22.4 23.5 23.7 25.6 47.7 22 51.3z" fill="url(#gradient1)" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#526484" />
                                    <stop offset="100%" stopColor="#0063ff" />
                                </linearGradient>
                            </defs>
                        </g>
                    </svg></button>
                    <button onClick={() => onDelete(row.id)} className='edit_icon_wepper'> <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 24 24">
                        <path d="M 10 2 L 9 3 L 4 3 L 4 5 L 5 5 L 5 20 C 5 20.522222 5.1913289 21.05461 5.5683594 21.431641 C 5.9453899 21.808671 6.4777778 22 7 22 L 17 22 C 17.522222 22 18.05461 21.808671 18.431641 21.431641 C 18.808671 21.05461 19 20.522222 19 20 L 19 5 L 20 5 L 20 3 L 15 3 L 14 2 L 10 2 z M 7 5 L 17 5 L 17 20 L 7 20 L 7 5 z M 9 7 L 9 18 L 11 18 L 11 7 L 9 7 z M 13 7 L 13 18 L 15 18 L 15 7 L 13 7 z" fill="#526484"></path>
                    </svg></button>
                </div>
            )
        }
    ];

    return (
        <Layout>
            {!isFormPage ? (
                <>
                    <div className="px-4">
                        <div className="admin-page mt-3 d-sm-flex justify-content-between align-items-center">
                            <div className="admin-heading" style={{ gap: "20px" }}>
                                <h4 className="mb-0">{data[0]?.serviceName}</h4>
                            </div>
                            <div className="add_btn">
                                <button onClick={() => navigate(getServiceDetailFormPath(serviceId, "add"))}>
                                    Add Services Details
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="datatable mt-3">
                        <DataTable
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 220px)"
                            columns={columns}
                            data={data || []}
                            progressPending={loading}
                            progressComponent={<p>Loading...</p>}
                            pagination
                            paginationPerPage={rowsPerPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            highlightOnHover
                        />
                    </div>
                </>
            ) : (
                <div className="px-4 service-detail-form-wrap">
                    <div className="admin-page mt-3 d-sm-flex justify-content-between align-items-center">
                        <div className="admin-heading add_btn d-flex flex-row align-items-center gap-2" style={{ gap: "20px" }}>
                            <button type="button" onClick={() => navigate(getServiceDetailFormPath(serviceId))}>
                                Back
                            </button>
                            <h4 className="mb-0">
                                {formData.id ? "Edit Services Details" : "Add Services Details"}
                            </h4>
                        </div>
                        <div className="add_btn d-flex flex-row align-items-end gap-2">
                            <button
                                type="button"
                                className="service-detail-add-section-btn"
                                onClick={addCustomSection}
                            >
                                Add Section
                            </button>
                        </div>
                    </div>

                    <div className="adminform service-detail-form-page mt-3">
                        {isEditPage && loading && !formData.id ? (
                            <p className="p-4 mb-0">Loading...</p>
                        ) : isEditPage && !loading && !formData.id ? (
                            <p className="p-4 mb-0">Service detail not found.</p>
                        ) : (
                            <div className="enForm">
                                {/* <div className="text-center form-heading">
                                    <h4 className="text-center form-heading">
                                        {formData.id ? "Edit Services Details" : "Add Services Details"}
                                    </h4>
                                </div> */}
                                <form onSubmit={onSave} className="p-4">
                                    {!serviceId && (
                                        <div className="form-group">
                                            <label>Select Service</label>
                                            <select
                                                name="serviceId"
                                                className="form-control"
                                                value={formData.serviceId}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">-- Select Service --</option>
                                                {serviceData.map((item) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {renderCustomSectionsEditor()}

                                    <h4>Section One</h4>
                                    <div className="form-group">
                                        <label>Section One Title</label>
                                        <input
                                            type="text"
                                            name="sectionOneTitle"
                                            className="form-control"
                                            value={formData.sectionOneTitle}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Section One Title'
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Section One Sub Title</label>
                                        <input
                                            type="text"
                                            name="sectionOneSubTitle"
                                            className="form-control"
                                            value={formData.sectionOneSubTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Section One Sub Title'
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Section Description</label>
                                        <textarea
                                            name="sectionOneShortDescription"
                                            className="form-control"
                                            value={formData.sectionOneShortDescription}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Section Description'
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="formFile">Section One Image {`(Size : 645px * 498px)`}</label>
                                        <input type="file" className="form-control" id="file" name="file" accept="image/png, image/jpeg" onChange={handleChange} ref={fileInputRef} />
                                    </div>
                                    <h4>Section Two</h4>
                                    <div className="form-group">
                                        <label>Section Two Title</label>
                                        <input
                                            type="text"
                                            name="sectionTwoTitle"
                                            className="form-control"
                                            value={formData.sectionTwoTitle}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Section Two Title'
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Section Two Sub Title</label>
                                        <input
                                            type="text"
                                            name="sectionTwoSubTitle"
                                            className="form-control"
                                            value={formData.sectionTwoSubTitle}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Section Two Sub Title'
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Section Two Description</label>
                                        <textarea
                                            name="sectionTwoDescription"
                                            className="form-control"
                                            value={formData.sectionTwoDescription}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Two Description'
                                        />
                                    </div>

                                    <h4>Section Third</h4>
                                    <div className="form-group">
                                        <label>Section Third Title</label>
                                        <input
                                            type="text"
                                            name="industriesTitle"
                                            className="form-control"
                                            value={formData.industriesTitle}
                                            onChange={handleChange}
                                            required
                                            placeholder='Enter Third Title'
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Section Third Sub Title</label>
                                        <input
                                            type="text"
                                            name="industriesSubTitle"
                                            className="form-control"
                                            value={formData.industriesSubTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Third Sub Title'
                                        />
                                    </div>

                                    <h4>AI Answer Box</h4>
                                    <div className="form-group">
                                        <label>AI Answer Box Title</label>
                                        <input
                                            type="text"
                                            name="AIAnswerBoxTitle"
                                            className="form-control"
                                            value={formData.AIAnswerBoxTitle}
                                            onChange={handleChange}
                                            placeholder='Enter AI Answer Box Title'
                                        />
                                    </div>

                                    {formData.AIAnswerBoxData.map((item, index) => (
                                        <div className="border rounded p-3 mb-3" key={`ai-answer-${index}`}>
                                            <div className="form-group">
                                                <label>Question</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.q}
                                                    onChange={(e) => handleArrayChange("AIAnswerBoxData", index, "q", e.target.value)}
                                                    placeholder='Enter Question'
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Answer</label>
                                                <textarea
                                                    className="form-control"
                                                    value={item.answer}
                                                    onChange={(e) => handleArrayChange("AIAnswerBoxData", index, "answer", e.target.value)}
                                                    placeholder='Enter Answer'
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeArrayRow("AIAnswerBoxData", index, { q: "", answer: "" })}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm mb-3"
                                        onClick={() => addArrayRow("AIAnswerBoxData", { q: "", answer: "" })}
                                    >
                                        Add AI Answer
                                    </button>

                                    <h4>Quick Facts</h4>
                                    <div className="form-group">
                                        <label>Quick Facts Title</label>
                                        <input
                                            type="text"
                                            name="quickFactsTitle"
                                            className="form-control"
                                            value={formData.quickFactsTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Quick Facts Title'
                                        />
                                    </div>

                                    {formData.quickFactsData.map((item, index) => (
                                        <div className="border rounded p-3 mb-3" key={`quick-fact-${index}`}>
                                            <div className="form-group">
                                                <label>Feature</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.feature}
                                                    onChange={(e) => handleArrayChange("quickFactsData", index, "feature", e.target.value)}
                                                    placeholder='Enter Feature'
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Details</label>
                                                <textarea
                                                    className="form-control"
                                                    value={item.details}
                                                    onChange={(e) => handleArrayChange("quickFactsData", index, "details", e.target.value)}
                                                    placeholder='Enter Details'
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Image</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    onChange={(e) => handleArrayChange("quickFactsData", index, "file", e.target.files?.[0] || null)}
                                                />
                                                {item.file ? (
                                                    <small className="text-muted d-block mt-1">{item.file.name}</small>
                                                ) : null}
                                            </div>
                                            <div className="form-group">
                                                <label>Color</label>
                                                <div className="d-flex gap-2 align-items-center">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={getColorPickerValue(item.color)}
                                                        onChange={(e) => handleArrayChange("quickFactsData", index, "color", e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={item.color || ""}
                                                        onChange={(e) => handleArrayChange("quickFactsData", index, "color", e.target.value)}
                                                        placeholder='Enter Color'
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeArrayRow("quickFactsData", index, createEmptyQuickFact())}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm mb-3"
                                        onClick={() => addArrayRow("quickFactsData", createEmptyQuickFact())}
                                    >
                                        Add Quick Fact
                                    </button>

                                    <h4>Case Studies</h4>
                                    <div className="form-group">
                                        <label>Case Studies Title</label>
                                        <input
                                            type="text"
                                            name="caseStudiesTitle"
                                            className="form-control"
                                            value={formData.caseStudiesTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Case Studies Title'
                                        />
                                    </div>

                                    {formData.caseStudiesData.map((item, index) => (
                                        <div className="border rounded p-3 mb-3" key={`case-study-${index}`}>
                                            <div className="form-group">
                                                <label>Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.name}
                                                    onChange={(e) => handleArrayChange("caseStudiesData", index, "name", e.target.value)}
                                                    placeholder='Enter Case Study Name'
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Link</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.link}
                                                    onChange={(e) => handleArrayChange("caseStudiesData", index, "link", e.target.value)}
                                                    placeholder='Enter Case Study Link'
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeArrayRow("caseStudiesData", index, { name: "", link: "" })}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm mb-3"
                                        onClick={() => addArrayRow("caseStudiesData", { name: "", link: "" })}
                                    >
                                        Add Case Study
                                    </button>

                                    <h4>Facts Features</h4>
                                    <div className="form-group">
                                        <label>Facts Features Title</label>
                                        <input
                                            type="text"
                                            name="factsFeaturesTitle"
                                            className="form-control"
                                            value={formData.factsFeaturesTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Facts Features Title'
                                        />
                                    </div>

                                    {formData.factsFeaturesData.map((item, index) => (
                                        <div className="border rounded p-3 mb-3" key={`facts-feature-${index}`}>
                                            <div className="form-group">
                                                <label>Title</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={item.title}
                                                    onChange={(e) => handleArrayChange("factsFeaturesData", index, "title", e.target.value)}
                                                    placeholder='Enter Title'
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Details</label>
                                                <textarea
                                                    className="form-control"
                                                    value={item.details}
                                                    onChange={(e) => handleArrayChange("factsFeaturesData", index, "details", e.target.value)}
                                                    placeholder='Enter Details'
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeArrayRow("factsFeaturesData", index, createEmptyFactsFeature())}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm mb-3"
                                        onClick={() => addArrayRow("factsFeaturesData", createEmptyFactsFeature())}
                                    >
                                        Add Facts Feature
                                    </button>

                                    <h4>Development Process</h4>
                                    <div className="form-group">
                                        <label>Development Process Title</label>
                                        <input
                                            type="text"
                                            name="developmentProcessTitle"
                                            className="form-control"
                                            value={formData.developmentProcessTitle}
                                            onChange={handleChange}
                                            placeholder='Enter Development Process Title'
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Development Process Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="developmentProcessImage"
                                            accept="image/png, image/jpeg"
                                            onChange={handleChange}
                                            ref={developmentProcessImageInputRef}
                                        />
                                    </div>

                                    {HTML_SECTION_FIELDS.map(renderHtmlSectionEditor)}

                                    <div className="d-flex form-buttons" style={{ gap: "15px" }}>
                                        <div className="submit-btn pt-2">
                                            <LoadingButton type="submit" loading={isSubmitting} loadingText={formData?.id ? 'Updating...' : 'Saving...'}>{formData?.id ? 'Update' : 'Submit'}</LoadingButton>
                                        </div>
                                        <div className="reset-btn pt-2">
                                            <button type="button" onClick={resetForm}>Reset</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Toaster position="top-right" />
        </Layout>
    );
};

export default ServiceDetails;
